import EnvironmentService from 'cio/services/environment';
import { MessagingChannelType, PresentationLocation } from 'cio/utils/setup-guide/messaging-channels';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import SetupGuideModel from 'cio/models/setup-guide';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import { action } from '@ember/object';
import AlertsService from 'cio/services/alerts';
import { titleize } from 'cio/helpers/titleize';

interface SetupGuideChannelsMessagingSetupArgs {
  presentationLocation?: PresentationLocation;
  onSetupComplete: () => void;
}

export default abstract class MessagingChannelSetup extends Component<SetupGuideChannelsMessagingSetupArgs> {
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @tracked setupGuide: SetupGuideModel | null = null;
  @service alerts!: AlertsService;

  abstract readonly channel: MessagingChannelType;
  abstract readonly showSuccessAlert: boolean;

  get messagingChannel() {
    return this.setupGuide?.get(this.channel) ?? null;
  }

  get onSetupComplete() {
    return this.args.onSetupComplete ?? (() => {});
  }

  get hasCompletedSetup() {
    return this.setupGuide != null && this.messagingChannel != null
      ? this.setupGuide.isChannelDoneWithSetup(this.messagingChannel)
      : false;
  }

  get isShowingInVWB() {
    return !this.isShowingInSetupGuide;
  }

  get isShowingInSetupGuide() {
    return this.args.presentationLocation === PresentationLocation.SetupGuide;
  }

  get showLocalSuccessAlert() {
    return this.isShowingInVWB && this.showSuccessAlert;
  }

  constructor(owner: unknown, args: SetupGuideChannelsMessagingSetupArgs) {
    super(owner, args);
    this.setupGuide = this.store.peekRecord('setup-guide', 'current');
  }

  @action
  protected markSetupComplete() {
    // this ideally should always be called from an extending class as super.markSetupComplete()
    // to ensure this default behavior is preserved. Custom behavior can be added by extending that method.
    if (this.isShowingInSetupGuide) {
      this.updateSetupGuide();
    }
    if (this.showLocalSuccessAlert) {
      this.handleAlert();
    }
    this.onSetupComplete();
  }

  @action
  private handleAlert() {
    this.alerts.success('channel.setup.complete', `${titleize([this.channel])} setup complete!`);
  }

  @action
  private updateSetupGuide() {
    let messagingChannelExists = this.setupGuide?.hasChannel(this.channel);
    if (!messagingChannelExists) {
      this.setupGuide?.addChannel(this.channel);
    }
    this.setupGuide?.markChannelComplete(this.channel);
  }
}
